/**
 * Единый статус анкеты (U-01, U-02, P-01): виден ли профиль и что сделать,
 * чтобы стал виден. Один компонент — одни и те же слова на всех экранах.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { profileVisibility } from '@entities/user/visibility'
import { api } from '@shared/api/client'
import { Button, Icon, cx, useToast } from '@shared/ui'

import { refreshMe } from '../../boot/bootstrap'
import { useAppStore } from '../../model/app-store'
import styles from './profile.module.css'

export function VisibilityCard({ compact = false }: { compact?: boolean }): React.ReactElement | null {
  const me = useAppStore((s) => s.me)
  const navigate = useNavigate()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  if (!me) return null
  const visibility = profileVisibility(me)

  const act = async (): Promise<void> => {
    const action = visibility.action
    if (!action) return

    if (action.to === 'resume') {
      setBusy(true)
      try {
        await api.POST('/me/resume')
        await refreshMe()
        toast.show('Анкета снова в подборке')
      } catch {
        toast.show('Не удалось снять с паузы', { tone: 'danger' })
      } finally {
        setBusy(false)
      }
      return
    }

    if (action.to === '/settings/restrictions') {
      void navigate({ to: '/settings/$section', params: { section: 'restrictions' } })
      return
    }

    void navigate({ to: action.to })
  }

  return (
    <div
      className={cx(
        styles['visibility'],
        styles[`visibility_${visibility.tone}`],
        compact && styles['visibilityCompact'],
      )}
      role="status"
    >
      <span className={styles['visibilityIcon']}>
        <Icon name={visibility.icon} size={20} />
      </span>

      <div className={styles['visibilityBody']}>
        <span className={styles['visibilityTitle']}>{visibility.title}</span>
        {!compact ? <span className={styles['visibilityText']}>{visibility.text}</span> : null}
      </div>

      {visibility.action ? (
        <Button
          size="sm"
          variant={visibility.tone === 'danger' ? 'secondary' : 'primary'}
          loading={busy}
          onClick={() => void act()}
        >
          {visibility.action.label}
        </Button>
      ) : null}
    </div>
  )
}
